import { QueryClient, QueryClientConfig } from '@tanstack/react-query';

/**
 * Create a TanStack Query client with default configuration
 * This is used for both client and server-side rendering
 */
export function createQueryClient(config?: QueryClientConfig): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Default stale time (1 minute)
        staleTime: 60 * 1000,
        
        // Don't refetch on window focus by default
        refetchOnWindowFocus: false,
        
        // Retry failed queries 1 time by default
        retry: 1,
        
        // Garbage collection time (5 minutes)
        gcTime: 5 * 60 * 1000,
      },
    },
    ...config,
  });
}

/**
 * Get the singleton query client instance
 * This ensures we're using the same instance throughout the app
 */
let queryClientInstance: QueryClient | null = null;

export function getQueryClient(): QueryClient {
  if (!queryClientInstance) {
    queryClientInstance = createQueryClient();
  }
  return queryClientInstance;
}

/**
 * Helper to use with React Router loaders
 * This allows us to use TanStack Query with React Router loaders
 */
export async function queryLoader<T>(
  queryClient: QueryClient,
  queryKey: any[],
  queryFn: () => Promise<T>
): Promise<T> {
  // Try to get from cache first
  const cachedData = queryClient.getQueryData<T>(queryKey);
  if (cachedData) {
    return cachedData;
  }

  // Otherwise fetch and cache
  return queryClient.fetchQuery({
    queryKey,
    queryFn
  });
}

/**
 * Suspense-compatible query function
 * This function will throw a promise if the data is not available
 * which will be caught by React Suspense
 */
export function fetchQuery<TData>(
  queryClient: QueryClient,
  queryKey: any[],
  queryFn: () => Promise<TData>
): TData {
  const state = queryClient.getQueryState<TData>(queryKey);
  
  // If the query exists and has data or an error, return the data or throw the error
  if (state) {
    if (state.status === 'error' && state.error) {
      throw state.error;
    }
    if (state.status === 'success' && state.data !== undefined) {
      return state.data;
    }
  }
  
  // Otherwise, fetch the data
  const promise = queryClient.fetchQuery<TData>({ queryKey, queryFn });
  throw promise;
}

/**
 * Helper to prefetch data for SSR
 * This is used to prefetch data for server-side rendering
 */
export async function prefetchQueries(
  queryClient: QueryClient,
  queries: Array<{
    queryKey: any[];
    queryFn: () => Promise<any>;
  }>
): Promise<void> {
  await Promise.all(
    queries.map(({ queryKey, queryFn }) =>
      queryClient.prefetchQuery({
        queryKey,
        queryFn
      })
    )
  );
}

/**
 * Dehydrate the query client state for client hydration
 * This is used to serialize the query client state for client hydration
 */
export function dehydrateQueryState(queryClient: QueryClient): string {
  // Get the cache data directly from the query client
  const queries = queryClient.getQueryCache().getAll();
  
  // If there are no queries, return an empty array string to avoid parsing issues
  if (queries.length === 0) {
    return '[]';
  }
  
  const dehydratedState = queries.map(query => ({
    queryKey: query.queryKey,
    state: query.state
  }));
  
  // Serialize the state
  return JSON.stringify(dehydratedState).replace(/</g, '\\u003c');
}

/**
 * Hydrate the query client with dehydrated state
 * This is used on the client side to rehydrate the query client
 */
export function hydrateQueryClient(queryClient: QueryClient, dehydratedState: any): void {
  if (!dehydratedState) {return;}

  let parsedState: any;
  try {
    if (typeof dehydratedState === "string") {
      parsedState = JSON.parse(dehydratedState);
    } else {
      parsedState = dehydratedState;
    }

    // If the parsed state is an empty array, there's nothing to hydrate
    if (!Array.isArray(parsedState) || parsedState.length === 0) {
      return;
    }

    // Set each query in the cache
    parsedState.forEach((query: any) => {
      if (query.queryKey && query.state) {
        queryClient.setQueryData(query.queryKey, query.state.data);
      }
    });
  } catch (error) {
    console.error('Error hydrating query client:', error);
  }
}
